import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  selectOverrideSheetsError,
  selectOverrideDataError,
  selectMappingDataError,
  selectCreateOverrideError,
  selectUpdateOverrideError,
} from '../../../store/reducers/feeManagement/feeManagementSelectors'
import { trackAndNotifyError } from '../utils/errorHandling'

const FeeManagementErrorHandler = ({ children }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const processedErrors = useRef(new Set())

  // Monitor all error states from the fee management slice
  const overrideSheetsError = useSelector(selectOverrideSheetsError)
  const overrideDataError = useSelector(selectOverrideDataError)
  const mappingDataError = useSelector(selectMappingDataError)
  const createOverrideError = useSelector(selectCreateOverrideError)
  const updateOverrideError = useSelector(selectUpdateOverrideError)

  // Handle different types of errors
  useEffect(() => {
    const handleSpecificError = (error, source) => {
      if (!error) {
        return
      }

      // Create a unique key for this error
      const errorKey = `${source}:${error.toString()}`

      // Skip if we've already processed this error
      if (processedErrors.current.has(errorKey)) {
        return
      }

      // Track that we've processed this error
      processedErrors.current.add(errorKey)

      // Include current route information for better debugging
      const routeInfo = {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
      }

      trackAndNotifyError(error, {
        source: `feeManagement.${source}`,
        context: { route: routeInfo },
        onClose: () => {
          // After a while, remove this error from our processed set
          // so it can be shown again if it happens again
          setTimeout(() => {
            processedErrors.current.delete(errorKey)
          }, 5000)
        },
      })
    }

    // Check for each type of error
    if (overrideSheetsError) {
      handleSpecificError(overrideSheetsError, 'overrideSheets')
    }
    if (overrideDataError) {
      handleSpecificError(overrideDataError, 'overrideData')
    }
    if (mappingDataError) {
      handleSpecificError(mappingDataError, 'mappingData')
    }
    if (createOverrideError) {
      handleSpecificError(createOverrideError, 'createOverride')
    }
    if (updateOverrideError) {
      handleSpecificError(updateOverrideError, 'updateOverride')
    }
  }, [
    overrideSheetsError,
    overrideDataError,
    mappingDataError,
    createOverrideError,
    updateOverrideError,
    dispatch,
    location,
  ])

  return children
}

export default FeeManagementErrorHandler
